import { CalendarDays, EyeOff, HandCoins, Receipt } from 'lucide-react';

import React, { useMemo } from 'react';

import type { SplitwiseFile } from '@splitfiesta/core';
import {
  formatDate,
  importOverFreeLimit,
  summarizeImportFile,
  summaryWarningLines,
} from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Avatar, Button } from '@splitfiesta/ui';

import { ActionButtonGroup } from '@/components/ui/ActionButtonGroup';

import { useAuthStore } from '@/stores/authStore';

import { ImportCard } from './ui/ImportCard';
import { ImportSection } from './ui/ImportSection';
import { type StatTile, StatTiles } from './ui/StatTiles';

interface Props {
  file: SplitwiseFile;
  /** The review's decisions so far, so coming back here shows their effect. */
  zeroRowAssignments?: Record<number, number>;
  categoryOverrides?: Record<string, string>;
  onBack: () => void;
  onContinue: () => void;
}

export function ImportSummaryStep({
  file,
  zeroRowAssignments,
  categoryOverrides,
  onBack,
  onContinue,
}: Props) {
  const { t, i18n } = useTranslation();
  useNamespace('import');

  const summary = useMemo(
    () => summarizeImportFile(file, zeroRowAssignments, categoryOverrides),
    [file, zeroRowAssignments, categoryOverrides]
  );

  const warningLines = useMemo(() => summaryWarningLines(file), [file]);

  const userPro = useAuthStore(state => state.user?.pro);
  const overLimit = importOverFreeLimit({
    expenseRows: summary.expenses,
    userPro,
  });

  const stats: StatTile[] = [
    {
      testId: 'import-summary-expenses',
      Icon: Receipt,
      value: summary.expenses,
      label: t('import:summary.expensesLabel'),
    },
    {
      testId: 'import-summary-payments',
      Icon: HandCoins,
      value: summary.payments,
      label: t('import:summary.paymentsLabel'),
    },
    {
      testId: 'import-summary-skipped',
      Icon: EyeOff,
      value: summary.skipped,
      label: t('import:summary.skippedLabel'),
    },
  ];

  return (
    <div className="space-y-6">
      <ImportSection title={t('import:summary.title')}>
        <StatTiles tiles={stats} />

        <ImportCard rows>
          <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <span className="shrink-0 text-sm text-gray-500 dark:text-gray-400">
              {t('import:summary.members')}
            </span>
            {/* Chips with initials instead of one comma-joined line: a long
                roster wraps into a ragged paragraph otherwise. */}
            <ul
              className="flex flex-wrap gap-1.5 sm:justify-end"
              data-testid="import-summary-members"
            >
              {summary.members.map(name => (
                <li
                  key={name}
                  className="flex items-center gap-1.5 rounded-full bg-gray-100 py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                >
                  <Avatar name={name} size="xs" />
                  {name}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:justify-between sm:gap-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('import:summary.currencies')}
            </span>
            <span
              className="text-sm font-medium text-gray-900 dark:text-white sm:text-right"
              data-testid="import-summary-currencies"
            >
              {summary.currencies
                .map(
                  currency =>
                    `${currency.code} · ${t('import:summary.rows', {
                      count: currency.rows,
                    })}`
                )
                .join(', ')}
            </span>
          </div>

          <div className="flex items-center gap-2 px-4 py-3">
            <CalendarDays
              className="h-4 w-4 shrink-0 text-gray-400"
              aria-hidden
            />
            <span
              className="text-sm text-gray-500 dark:text-gray-400"
              data-testid="import-summary-range"
            >
              {t('import:summary.dateRange', {
                from: formatDate(summary.dateRange.from, i18n.language),
                to: formatDate(summary.dateRange.to, i18n.language),
              })}
            </span>
          </div>
        </ImportCard>

        {(warningLines.length > 0 || overLimit) && (
          <ImportCard
            tone="warning"
            padded
            data-testid="import-summary-warnings"
          >
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {t('import:summary.warningsTitle')}
            </h3>
            <ul className="list-disc space-y-1 pl-5 text-sm text-amber-800 dark:text-amber-200">
              {overLimit && (
                <li data-testid="import-summary-over-limit">
                  {t('import:summary.overLimit', { limit: overLimit.limit })}
                </li>
              )}
              {warningLines.map((line, index) => (
                <li key={`${line.key}-${index}`}>{t(line.key, line.params)}</li>
              ))}
            </ul>
          </ImportCard>
        )}
      </ImportSection>

      <ActionButtonGroup>
        <Button
          variant="secondary"
          data-testid="import-summary-back"
          onClick={onBack}
        >
          {t('common:actions.back')}
        </Button>
        <Button
          variant="primary"
          data-testid="import-summary-continue"
          onClick={onContinue}
        >
          {t('import:summary.continue')}
        </Button>
      </ActionButtonGroup>
    </div>
  );
}
